import { ComponentType, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ActivableRenderer, Portal, withClickAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { ANIMATION_DURATION } from '@optimus-web/local-tokens';
import { OpenableOverlay } from '../OpenableOverlay';
import { isNil } from '../utils/func';
import { DialogBaseProps, DialogProps } from './DialogProps';
import {
    ActionsButtonsStack,
    BackdropElement,
    DialogActionsElement,
    DialogButtonElement,
    DialogCloseButton,
    DialogContainerElement,
    DialogContentElement,
    DialogElement,
    DialogFooterWithDetail,
    DialogHeader,
    DialogTitle,
    FooterDetailContainer,
} from './style';

const DialogActivatorFunction = ActivableRenderer<React.Component<DialogProps>, DialogProps>({
    delay: ANIMATION_DURATION.default,
});

const Dialog = ({
    size,
    active,
    modal = true,
    position,
    contentWithoutPadding,
    actions,
    footerDetail,
    portalContainerId,
    onClose,
    title,
    children,
    innerRef,
    inline,
    anchorElement,
    registerClickAway,
    deregisterClickAway,
    noMaxWidth = false,
    mobileFullscreen = false,
    ...other
}: DialogProps) => {
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (onClose && inline) {
            registerClickAway({ handler: onClose as () => void, roots: [anchorElement, () => dialogRef.current] });
        }
        return () => {
            if (inline) {
                deregisterClickAway();
            }
        };
    }, [registerClickAway, deregisterClickAway, onClose, anchorElement, inline]);

    // handle screen height on iphone (without toolbars and keyboard)
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const originalHeight = useRef('');
    const handleResize = useCallback(() => {
        if (!window.visualViewport) return;
        window.document.body.style.height = `${window.visualViewport.height}px`;
        setWindowHeight(window.visualViewport.height);
        window.scrollTo(0, 0);
    }, [setWindowHeight]);
    useEffect(() => {
        if (window.visualViewport != null && modal) {
            originalHeight.current = window.document.body.style.height;
            window.visualViewport.addEventListener('resize', handleResize);
        }
        return () => {
            if (window.visualViewport != null && modal) {
                window.visualViewport.removeEventListener('resize', handleResize);
                window.document.body.style.height = originalHeight.current;
            }
        };
    }, [handleResize, modal]);

    const actionsButtons =
        actions && actions.length > 0
            ? actions.map((action, index) => (
                  <DialogButtonElement
                      key={action.key || index}
                      data-test-button-action={action.key || index}
                      {...action}
                  />
              ))
            : null;

    const actionsElement =
        actionsButtons && isNil(footerDetail) ? (
            <DialogActionsElement>{actionsButtons}</DialogActionsElement>
        ) : (
            actionsButtons && <ActionsButtonsStack vertical="up-to-extra-small">{actionsButtons}</ActionsButtonsStack>
        );

    // To keep backwards compatibility with dialogFactory DialogActions, we need to not render the inner div.
    const FooterContainer = isNil(footerDetail) ? Fragment : DialogFooterWithDetail;

    const footerComponent =
        !isNil(footerDetail) || !isNil(actionsElement) ? (
            <FooterContainer>
                <FooterDetailContainer>{footerDetail}</FooterDetailContainer>
                {actionsElement}
            </FooterContainer>
        ) : null;

    const ContentWrapperElement = inline ? 'div' : Fragment;

    const content = (
        <ContentWrapperElement ref={inline ? dialogRef : undefined}>
            {title || onClose ? (
                <DialogHeader
                    distribution="space-between"
                    itemAlignment="center"
                    position={position}
                    data-test-dialog-header
                >
                    <DialogTitle ellipsis maxLines={3} position={position}>
                        {title}
                    </DialogTitle>
                    {onClose && (
                        <DialogCloseButton
                            icon={IconName.CrossClose}
                            onClick={onClose}
                            position={position}
                            data-test-button-close
                            variant="tertiary"
                            size="small"
                        />
                    )}
                </DialogHeader>
            ) : null}
            <DialogContentElement position={position} withoutPadding={contentWithoutPadding} data-test-dialog-content>
                {children}
            </DialogContentElement>
            {footerComponent}
        </ContentWrapperElement>
    );

    if (inline) {
        return (
            <OpenableOverlay opened={active} anchorElement={anchorElement} placement="auto-start" minWidth={320}>
                {content}
            </OpenableOverlay>
        );
    }

    return (
        <Portal containerId={portalContainerId} {...other}>
            <DialogContainerElement height={windowHeight}>
                {active && modal && <BackdropElement onClick={onClose} />}
                {active && (
                    <DialogElement
                        noMaxWidth={noMaxWidth}
                        size={size}
                        position={position}
                        ref={innerRef}
                        mobileFullscreen={mobileFullscreen}
                        data-test-dialog
                    >
                        {content}
                    </DialogElement>
                )}
            </DialogContainerElement>
        </Portal>
    );
};

export default withClickAway(DialogActivatorFunction(Dialog)) as ComponentType<DialogBaseProps>;
